import { Vector3 } from "flavr-math";
import { RoomChangedMessage, EnterRoomMessage, ExitCurrentRoomMessage, LayoutCreationFinishedMessage, } from "flavr-common";
import { EndSessionMessage, StartSessionMessage } from "@messages/session";
import { WebXRPresentingMessage } from "@messages/xr/WebXRMessages";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { ENVIRONMENT_IS_WORKER } from "@messages/environment";
import { FrameUpdateMessage } from "@messages/rendering";
import { ActivateModuleMessage, DeactivateModuleMessage } from "@messages/module";
import { MessagingBase } from "./MessagingBase";
// TODO: Try to get rid of imports from other modules if possible by moving the relevant dependencies into @messages
import { busService, HandshakeModuleMessage, ResponseHandshakeModuleMessage, SystemInitMessage, } from "../bus";
export var PresentationMode;
(function (PresentationMode) {
    PresentationMode[PresentationMode["DESKTOP"] = 0] = "DESKTOP";
    PresentationMode[PresentationMode["VR"] = 1] = "VR";
})(PresentationMode || (PresentationMode = {}));
/**
 * A `NewModule` is a logically self-contained part of the application. Modules usually encapsulate one feature or
 * responsibility and communicate with other modules through `BusMessage`s.
 *
 * A module can react to the system in two ways:
 * - by implementing life cycle hooks such as `onInit`, `onSessionStarted`, `onRoomLoaded`, `onPreRender`, or
 *   `onPresentationModeChanged`
 * - by registering handlers, either manually or with decorators such as `@MessageHandler`, `@MessageRequestHandler`,
 *   `@MessageOrMessageRequestHandler`, `@FacetHandler`, `@FacetRemoveHandler`, or `@ModelEventHandler`
 *
 * The constructor must call `super("MyUniqueModuleName")` with a unique module name. This name identifies the module
 * on the bus and is also used for things like module activation and the startup handshake.
 *
 * A module can run on the main thread or inside a worker.
 *
 * Main thread module registration:
 * ```ts
 * // FullAppConfig.ts
 * import { SpinningModule } from "@modules/spinning/SpinningModule"
 *
 * workerFactory.registerModule(SpinningModule)
 * ```
 *
 * Worker module registration:
 * ```ts
 * // client/src/worker/my.worker.ts
 * import { ModuleWorker } from "@modules/module"
 * import { SpinningModule } from "@modules/spinning/SpinningModule"
 *
 * class MyWorker extends ModuleWorker {
 *   protected initModules(): void {
 *     new SpinningModule()
 *   }
 * }
 * ```
 *
 * In the current setup, main thread modules are registered in `FullAppConfig`, while worker modules are instantiated in
 * the corresponding file in `client/src/worker/` inside `initModules()`.
 *
 * The order also matters:
 * - main thread modules are created in the order they are passed to `workerFactory.registerModule(...)`
 * - worker modules are created in the order they are instantiated in `initModules()`
 *
 * Regardless of creation order, all modules are created first. Afterwards the `SystemInitMessage` triggers `onInit` for
 * all of them.
 *
 * The following example shows a small but realistic module setup: a custom `SpinableFacet` stores a spin axis and
 * speed, and a `SpinningModule` keeps track of all matching models and rotates them every frame.
 * ```ts
 * // SpinableFacet.ts
 * // Other facet boilerplate omitted for brevity.
 * import { type Number3 } from "flavr-common"
 *
 * export class SpinableFacet {
 *   direction: Number3
 *   speed: number
 * }
 *
 * // SpinningModule.ts
 * import { type UID } from "flavr-common"
 * import { Quaternion, Vector3 } from "flavr-math"
 * import { FacetHandler, FacetRemoveHandler, NewModule } from "@modules/module"
 * import { modelPool } from "@modules/model"
 * import { clockService } from "@modules/xr/ClockService"
 * import { SpinableFacet } from "./SpinableFacet"
 *
 * const TMP_AXIS = new Vector3()
 * const TMP_ROTATION = new Quaternion()
 * const TMP_ORIENTATION = new Quaternion()
 *
 * export class SpinningModule extends NewModule {
 *   private readonly spinners = new Map<UID, SpinableFacet>()
 *
 *   constructor() {
 *     super("SpinningModule")
 *   }
 *
 *   @FacetHandler(SpinableFacet)
 *   protected handleSpinableFacet(uid: UID, facet: SpinableFacet): void {
 *     this.spinners.set(uid, facet)
 *   }
 *
 *   @FacetRemoveHandler(SpinableFacet)
 *   protected handleSpinableFacetRemoved(uid: UID, _facet: SpinableFacet): void {
 *     this.spinners.delete(uid)
 *   }
 *
 *   protected onPreRender(): void {
 *     const deltaTime = clockService.getDeltaToLastFrameInSeconds()
 *
 *     this.spinners.forEach((facet, uid) => {
 *       const model = modelPool.getModel(uid)
 *       if (model === undefined) return
 *
 *       TMP_AXIS.fromArray(facet.direction)
 *       if (TMP_AXIS.lengthSq() === 0) return
 *       TMP_AXIS.normalize()
 *
 *       TMP_ROTATION.setFromAxisAngle(TMP_AXIS, facet.speed * deltaTime)
 *       model.setOrientation(model.getOrientation(TMP_ORIENTATION).multiply(TMP_ROTATION))
 *     })
 *   }
 * }
 * ```
 *
 * @see BusMessages
 */
export class NewModule extends MessagingBase {
    /**
     * @param moduleID - unique ID that has to be different for each module
     */
    constructor(moduleID) {
        // Create bus connection
        super(busService.connection(moduleID));
        this.moduleID = moduleID;
        /// Module handshake and system init helper methods ///
        this.handleHandshakeModuleMessage = (msg, askId) => {
            if (msg.moduleId !== this.moduleID)
                return;
            this.connection.answer(askId, new ResponseHandshakeModuleMessage(msg.moduleId));
            this.connection.unsubscribe(this.handshakeModuleSubscription);
        };
        this.handleSystemInitSubscription = (e) => {
            var _a;
            if (SystemInitMessage.is(e)) {
                this.connection.onSystemInitReceived();
                (_a = this.onInit) === null || _a === void 0 ? void 0 : _a.call(this, e.locationHref);
                this.connection.unsubscribe(this.systemInitSubscription);
                // Setup some methods that should only be run after the module has been initialized
                this.setupLifeCycleMethodsAfterInitialization();
            }
            else {
                throw new Error(`unexpected event should match SystemInitMessage! {JSON.stringify(e)}`);
            }
        };
        // Register module handshake and system init event handlers
        this.handshakeModuleSubscription = this.connection.subscribeForAskRequest(HandshakeModuleMessage, this.handleHandshakeModuleMessage, `:${this.moduleID}`);
        this.systemInitSubscription = this.connection.subscribe(SystemInitMessage.typestatic, this.handleSystemInitSubscription);
        /// Register life cycle methods ///
        // Activation / deactivation life cycle methods
        if (this.onActivate !== undefined) {
            const callbackMethod = this.onActivate.bind(this);
            this.connection.subscribeByType(ActivateModuleMessage, (message) => {
                if (message.moduleName !== this.moduleID)
                    return;
                callbackMethod();
            });
        }
        if (this.onDeactivate !== undefined) {
            const callbackMethod = this.onDeactivate.bind(this);
            this.connection.subscribeByType(DeactivateModuleMessage, (message) => {
                if (message.moduleName !== this.moduleID)
                    return;
                callbackMethod();
            });
        }
        // Session life cycle methods
        if (this.onSessionStarted !== undefined) {
            const callbackMethod = this.onSessionStarted.bind(this);
            this.connection.subscribeByType(StartSessionMessage, (message) => {
                callbackMethod(message.initialRoomId);
            });
        }
        if (this.onSessionEnded !== undefined) {
            const callbackMethod = this.onSessionEnded.bind(this);
            this.connection.subscribeByType(EndSessionMessage, () => {
                callbackMethod();
            });
        }
        // Room/world life cycle methods
        if (this.onExitCurrentRoom !== undefined) {
            const callbackMethod = this.onExitCurrentRoom.bind(this);
            this.connection.subscribeByType(ExitCurrentRoomMessage, (message) => {
                callbackMethod(message.flying);
            });
        }
        if (this.onEnterNewRoom !== undefined) {
            const callbackMethod = this.onEnterNewRoom.bind(this);
            this.connection.subscribeByType(EnterRoomMessage, (message) => {
                callbackMethod(message.uid, message.params);
            });
        }
        if (this.onRoomLoaded !== undefined) {
            const callbackMethod = this.onRoomLoaded.bind(this);
            this.connection.subscribeByType(RoomChangedMessage, (message) => {
                callbackMethod(message.uid, message.prevUid);
            });
        }
        if (this.onWorldLoaded !== undefined) {
            const callbackMethod = this.onWorldLoaded.bind(this);
            this.connection.subscribeByType(LayoutCreationFinishedMessage, (message) => {
                callbackMethod(message.layouterIdentifier, message.uid, message.isFirstCreation);
            });
        }
        // VR life cycle methods
        if (this.onPresentationModeChanged !== undefined) {
            const callbackMethod = this.onPresentationModeChanged.bind(this);
            this.connection.subscribeByType(WebXRPresentingMessage, (message) => {
                const mode = message.isPresenting ? PresentationMode.VR : PresentationMode.DESKTOP;
                const viewPosition = (message.position !== undefined) ? new Vector3().fromArray(message.position) : new Vector3();
                const viewDirection = (message.direction !== undefined) ? new Vector3().fromArray(message.direction) : new Vector3();
                callbackMethod(mode, viewPosition, viewDirection);
            });
        }
    }
    /// Getters and setters ///
    /**
     * Returns the module ID
     */
    getName() { return this.moduleID; }
    /// Private setup methods after initialization ///
    setupLifeCycleMethodsAfterInitialization() {
        // Render life cycle methods
        if (this.onPreRender !== undefined) {
            if (ENVIRONMENT_IS_WORKER) {
                this.connection.subscribeByType(FrameUpdateMessage, this.onPreRender.bind(this));
                return;
            }
            animationFrame$.onPreRender$.add(this.onPreRender.bind(this));
        }
    }
}
